import { useRef, useState } from 'react';
import { FlatList, Pressable, type NativeSyntheticEvent, type NativeScrollEvent } from 'react-native';
import { Column, Row } from './box';
import { PCImage } from './image';
import { Text } from './text';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { isMobileNative } from 'app/utils/web-helpers';
import { ArrowRight } from '../icons/arrow-right';
import { useTranslation } from 'react-i18next';

export interface SwiperImage {
  id?: string;
  url: string;
}

interface ImageSwiperProps {
  images: SwiperImage[];
  height?: number;
  onPress?: (index: number) => void;
  onActiveIndexChange?: (index: number) => void;
  backgroundColor?: string;
  shareIcon?: React.ReactNode;
  paginationBottom?: number;
  paginationTop?: number;
}

export function ImageSwiper({
  images,
  height = 174,
  onPress,
  onActiveIndexChange,
  backgroundColor = 'transparent',
  shareIcon,
  paginationBottom = 8,
  paginationTop,
}: ImageSwiperProps) {
  const { t } = useTranslation('accessibility');
  const [activeIndex, setActiveIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const flatListRef = useRef<FlatList<SwiperImage>>(null);

  const updateActiveIndexFromOffset = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (!containerWidth) return;

    const nextIndex = Math.round(event.nativeEvent.contentOffset.x / containerWidth);
    const clampedIndex = Math.max(0, Math.min(nextIndex, images.length - 1));

    setActiveIndex(clampedIndex);
    onActiveIndexChange?.(clampedIndex);
  };

  const scrollToIndex = (index: number) => {
    const clampedIndex = Math.max(0, Math.min(index, images.length - 1));
    flatListRef.current?.scrollToIndex({ index: clampedIndex, animated: true });
    setActiveIndex(clampedIndex);
    onActiveIndexChange?.(clampedIndex);
  };

  const renderItem = ({ item, index }: { item: SwiperImage; index: number }) => {
    const imageUrl = getImageVariantUrl(item.url, 'medium');

    return (
      <Pressable
        onPress={() => onPress?.(index)}
        style={{
          width: containerWidth,
          height,
          justifyContent: 'center',
        }}
      >
        <PCImage
          cachePolicy="memory-disk"
          contentFit="cover"
          height={height}
          width={containerWidth}
          recyclingKey={`${item.url}-swiper`}
          source={{ uri: imageUrl }}
          style={{ backgroundColor }}
          useOwnCache
        />
        {shareIcon}
      </Pressable>
    );
  };

  if (images.length === 0) {
    return <Column style={{ height, width: '100%', backgroundColor }} />;
  }

  if (images.length <= 1) {
    const singleImage = images[0];
    const singleUrl = singleImage ? getImageVariantUrl(singleImage.url, 'medium') : undefined;

    return (
      <Pressable onPress={() => onPress?.(0)} style={{ width: '100%', height, justifyContent: 'center' }}>
        <PCImage
          cachePolicy="memory-disk"
          contentFit="cover"
          height={height}
          recyclingKey={singleImage ? `${singleImage.url}-swiper` : undefined}
          source={{ uri: singleUrl }}
          style={{ backgroundColor }}
          useOwnCache
        />
        {shareIcon}
      </Pressable>
    );
  }

  return (
    <Column style={{ height }} onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}>
      {containerWidth > 0 ? (
        <FlatList
          scrollEnabled={isMobileNative}
          ref={flatListRef}
          data={images}
          renderItem={renderItem}
          keyExtractor={(item) => item.id ?? item.url}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onMomentumScrollEnd={updateActiveIndexFromOffset}
          onScrollEndDrag={updateActiveIndexFromOffset}
          getItemLayout={(_data, index) => ({ length: containerWidth, offset: containerWidth * index, index })}
        />
      ) : null}
      {!isMobileNative && activeIndex > 0 ? (
        <Pressable
          accessibilityLabel={t('imageSwiper.previous')}
          accessibilityRole="button"
          onPress={() => scrollToIndex(activeIndex - 1)}
          style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            marginTop: -15,
            zIndex: 1,
          }}
        >
          <ArrowRight reverse color="white" arrowColor="black" />
        </Pressable>
      ) : null}
      {!isMobileNative && activeIndex < images.length - 1 ? (
        <Pressable
          accessibilityLabel={t('imageSwiper.next')}
          accessibilityRole="button"
          onPress={() => scrollToIndex(activeIndex + 1)}
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            marginTop: -15,
            zIndex: 1,
          }}
        >
          <ArrowRight color="white" arrowColor="black" />
        </Pressable>
      ) : null}
      <Row
        testID="image-swiper-pagination-container"
        position="absolute"
        {...(paginationTop === undefined ? { bottom: paginationBottom } : { top: paginationTop })}
        alignSelf="center"
        alignItems="center"
      >
        <Row
          gap="spacing.general.x1"
          style={{
            backgroundColor: 'rgba(0,0,0,0.35)',
            borderRadius: 99,
            paddingHorizontal: images.length > 5 ? 9 : 7,
            paddingVertical: 5,
            alignItems: 'center',
          }}
        >
          {images.length > 5 ? (
            <Text style={{ color: 'rgba(255,255,255,1)', fontSize: 11, fontWeight: '600', lineHeight: 14 }}>
              {activeIndex + 1} / {images.length}
            </Text>
          ) : (
            images.map((img, index) => (
              <Column
                key={img.id ?? img.url}
                width={6}
                height={6}
                borderRadius="radius.xl"
                style={{ backgroundColor: index === activeIndex ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.5)' }}
              />
            ))
          )}
        </Row>
      </Row>
    </Column>
  );
}
